import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Table,
  Button,
  Drawer,
  Form,
  Input,
  Select,
  Switch,
  Space,
  message,
  Pagination,
  Alert,
  Spin,
  Tag,
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { FiSearch } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { PERMISSIONS } from '../../config/permissions';
import DashboardPageWrapper from '../../components/layout/DashboardPageWrapper';
import whatsappGroupService from '../../services/whatsappGroupService';
import partnerNetworkService from '../../services/partnerNetworkService';
import {
  getWhatsAppGroupsColumns,
  ACTIVE_FILTER_OPTIONS,
  BOOL_FILTER_OPTIONS,
  FEATURE_FILTERS,
} from './commons/whatsappGroups.helpers';

const INITIAL_FEATURE_FILTERS = {
  priceListFilter: '',
  bondDeckFilter: '',
  inventoryFilter: '',
  chatbotFilter: '',
};

export default function WhatsAppGroupsPage() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canCreate = isSuperAdmin || hasPermission(PERMISSIONS.PRICING_WHATSAPP_GROUPS_CREATE);
  const canEdit = isSuperAdmin || hasPermission(PERMISSIONS.PRICING_WHATSAPP_GROUPS_EDIT);

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [featureFilters, setFeatureFilters] = useState(INITIAL_FEATURE_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [total, setTotal] = useState(0);
  const [form] = Form.useForm();

  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogGroups, setCatalogGroups] = useState([]);
  const [catalogError, setCatalogError] = useState('');
  const [userOptions, setUserOptions] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [rmOptions, setRmOptions] = useState([]);
  const [rmOptionsLoading, setRmOptionsLoading] = useState(false);
  const [derivedRmName, setDerivedRmName] = useState(null);
  const [derivedRmLoading, setDerivedRmLoading] = useState(false);

  const selectedPartnerId = Form.useWatch('partner_id', form);
  const hasLinkedPartner = Boolean(selectedPartnerId);
  const isEdit = Boolean(editing);

  const loadEligibleRms = async () => {
    setRmOptionsLoading(true);
    try {
      const list = await whatsappGroupService.fetchRmOptions();
      setRmOptions(Array.isArray(list) ? list : []);
    } catch (e) {
      setRmOptions([]);
    } finally {
      setRmOptionsLoading(false);
    }
  };

  const loadDerivedRmForPartner = async (partnerUuid) => {
    if (!partnerUuid) {
      setDerivedRmName(null);
      return;
    }
    setDerivedRmLoading(true);
    try {
      const res = await partnerNetworkService.getRmForPartner(partnerUuid);
      const rm = res?.data ?? res;
      const name = rm?.name ?? rm?.rm_name ?? null;
      setDerivedRmName(name);
    } catch (e) {
      setDerivedRmName(null);
    } finally {
      setDerivedRmLoading(false);
    }
  };

  useEffect(() => {
    if (!drawerOpen) return;
    if (hasLinkedPartner) {
      form.setFieldValue('rm_user_id', undefined);
      loadDerivedRmForPartner(selectedPartnerId);
    } else {
      setDerivedRmName(null);
    }
  }, [drawerOpen, hasLinkedPartner, selectedPartnerId, form]);

  const loadGroups = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: pageSize,
      };
      if (searchInput.trim()) params.search = searchInput.trim();
      if (activeFilter) params.is_active = activeFilter;
      FEATURE_FILTERS.forEach(({ key, param }) => {
        const value = featureFilters[key];
        if (value) params[param] = value;
      });

      const { groups: list, pagination } = await whatsappGroupService.list(params);
      setGroups(list);
      setTotal(pagination?.total ?? list.length);
    } catch (e) {
      message.error(e?.message || 'Failed to load WhatsApp groups');
      setGroups([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchInput, activeFilter, featureFilters]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const loadWhapiCatalog = async () => {
    setCatalogLoading(true);
    setCatalogError('');
    try {
      const list = await whatsappGroupService.fetchWhapiCatalog();
      setCatalogGroups(list);
      if (!list.length) {
        setCatalogError('No WhatsApp groups found on the connected channel.');
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to load groups from WhatsApp';
      setCatalogError(msg);
      setCatalogGroups([]);
    } finally {
      setCatalogLoading(false);
    }
  };

  const searchUsers = async (term) => {
    setUserSearchLoading(true);
    try {
      const users = await whatsappGroupService.fetchUserOptions({
        search: term || '',
        limit: 20,
      });
      setUserOptions(users);
    } catch (e) {
      setUserOptions([]);
    } finally {
      setUserSearchLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      is_active: true,
      price_list_to_be_sent: false,
      bond_deck_to_be_sent: false,
      inventory_position_to_be_sent: false,
      chatbot_enabled: false,
      special_pricing_approval_enabled: false,
      rm_price_approval_enabled: false,
    });
    setCatalogGroups([]);
    setCatalogError('');
    setDerivedRmName(null);
    setDrawerOpen(true);
    loadWhapiCatalog();
    searchUsers('');
    loadEligibleRms();
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      group_name: record.group_name,
      is_active: record.is_active,
      price_list_to_be_sent: record.price_list_to_be_sent,
      bond_deck_to_be_sent: record.bond_deck_to_be_sent,
      inventory_position_to_be_sent: record.inventory_position_to_be_sent,
      chatbot_enabled: record.chatbot_enabled,
      special_pricing_approval_enabled: record.special_pricing_approval_enabled,
      rm_price_approval_enabled: record.rm_price_approval_enabled,
      partner_id: record.partner_id ?? null,
      rm_user_id:
        record.assigned_rm?.source === 'MANUAL' ? record.assigned_rm.id : null,
    });
    setDrawerOpen(true);
    if (record.partner_id && record.partner) {
      setUserOptions([{ id: record.partner_id, ...record.partner }]);
      if (record.assigned_rm?.source === 'PARTNER') {
        setDerivedRmName(record.assigned_rm.name);
      } else {
        loadDerivedRmForPartner(record.partner_id);
      }
    } else {
      searchUsers('');
      setDerivedRmName(null);
    }
    loadEligibleRms();
  };

  const onDrawerClose = () => {
    setDrawerOpen(false);
    setEditing(null);
    form.resetFields();
    setCatalogError('');
    setDerivedRmName(null);
  };

  const onWhapiGroupSelect = (whatsappGroupId) => {
    const picked = catalogGroups.find((g) => g.whatsapp_group_id === whatsappGroupId);
    if (picked) {
      form.setFieldsValue({
        whatsapp_group_id: picked.whatsapp_group_id,
        group_name: picked.group_name,
      });
    }
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const payload = {
        group_name: (values.group_name || '').trim(),
        is_active: values.is_active,
        price_list_to_be_sent: values.price_list_to_be_sent,
        bond_deck_to_be_sent: values.bond_deck_to_be_sent,
        inventory_position_to_be_sent: values.inventory_position_to_be_sent,
        chatbot_enabled: values.chatbot_enabled,
        special_pricing_approval_enabled: values.special_pricing_approval_enabled,
        rm_price_approval_enabled: values.rm_price_approval_enabled,
        partner_id: values.partner_id ?? null,
      };

      if (!values.partner_id) {
        payload.rm_user_id = values.rm_user_id ?? null;
      }

      if (isEdit) {
        const res = await whatsappGroupService.update(editing.id, payload);
        if (res?.success === false) {
          message.error(res?.message || 'Update failed');
          return;
        }
        message.success('WhatsApp group updated');
      } else {
        const res = await whatsappGroupService.create({
          ...payload,
          whatsapp_group_id: values.whatsapp_group_id,
        });
        if (res?.success === false) {
          message.error(res?.message || 'Create failed');
          return;
        }
        message.success('WhatsApp group registered');
      }

      onDrawerClose();
      loadGroups();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (record) => {
    try {
      const res = await whatsappGroupService.remove(record.id);
      if (res?.success === false) {
        message.error(res?.message || 'Delete failed');
        return;
      }
      message.success('WhatsApp group removed');
      loadGroups();
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Delete failed');
    }
  };

  const registeredIds = new Set(groups.map((g) => g.whatsapp_group_id));
  const catalogOptions = catalogGroups
    .filter((g) => !registeredIds.has(g.whatsapp_group_id))
    .map((g) => ({
      value: g.whatsapp_group_id,
      label: `${g.group_name}${g.size != null ? ` (${g.size} members)` : ''}`,
    }));

  const columns = getWhatsAppGroupsColumns({
    canEdit,
    onEdit: openEdit,
    onDelete,
  });

  const hasFeatureFilters = Object.values(featureFilters).some(Boolean);

  const onFeatureFilterChange = (key, value) => {
    setFeatureFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFeatureFilters = () => {
    setFeatureFilters(INITIAL_FEATURE_FILTERS);
    setPage(1);
  };

  return (
    <DashboardPageWrapper>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">WhatsApp groups</h1>
          <p className="text-sm text-gray-500 mt-1">
            Register channel groups for future price-list and bond deck distribution.
          </p>
        </div>
        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add group
          </Button>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-100 flex flex-col gap-3 bg-gray-50/50">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative w-full sm:max-w-md">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or WhatsApp group ID"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  setPage(1);
                }}
                className="w-full h-[38px] pl-9 pr-4 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all bg-white"
              />
            </div>
            <Select
              value={activeFilter}
              onChange={(v) => {
                setActiveFilter(v);
                setPage(1);
              }}
              options={ACTIVE_FILTER_OPTIONS}
              className="w-36"
              placeholder="Status"
            />
            <Button icon={<ReloadOutlined />} onClick={loadGroups} loading={loading}>
              Refresh
            </Button>
            <p className="text-xs text-gray-500 sm:ml-auto">{total} groups</p>
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Features
            </span>
            {FEATURE_FILTERS.map(({ key, label }) => (
              <div key={key} className="flex items-center gap-1.5">
                <span className="text-xs text-gray-600 whitespace-nowrap">{label}</span>
                <Select
                  value={featureFilters[key]}
                  onChange={(v) => onFeatureFilterChange(key, v)}
                  options={BOOL_FILTER_OPTIONS}
                  className="w-[5.5rem]"
                />
              </div>
            ))}
            {hasFeatureFilters && (
              <Button type="link" size="small" onClick={clearFeatureFilters} className="px-1">
                Clear feature filters
              </Button>
            )}
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={groups}
          rowKey={(r) => r.id}
          loading={loading}
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
          className="[&_.ant-table-thead>tr>th]:whitespace-nowrap [&_.ant-table-tbody>tr>td]:whitespace-nowrap"
        />

        <div className="flex items-center justify-end px-4 py-3 border-t border-gray-100">
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            showSizeChanger
            pageSizeOptions={['10', '15', '30', '50']}
            onChange={(p, ps) => {
              setPage(p);
              if (ps !== pageSize) {
                setPageSize(ps);
                setPage(1);
              }
            }}
            showTotal={(t, range) => (
              <span className="text-xs text-gray-500">
                {range[0]}–{range[1]} of {t}
              </span>
            )}
          />
        </div>
      </div>

      <Drawer
        title={isEdit ? 'Edit WhatsApp group' : 'Register WhatsApp group'}
        width={480}
        open={drawerOpen}
        onClose={onDrawerClose}
        extra={
          <Space>
            <Button onClick={onDrawerClose}>Cancel</Button>
            <Button type="primary" loading={submitting} onClick={onSubmit}>
              {isEdit ? 'Save' : 'Register'}
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          {!isEdit && (
            <>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">WhatsApp group</span>
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  loading={catalogLoading}
                  onClick={loadWhapiCatalog}
                >
                  Refresh from WhatsApp
                </Button>
              </div>
              {catalogError && (
                <Alert
                  type="warning"
                  showIcon
                  className="mb-3"
                  message={catalogError}
                  description={
                    catalogError.includes('not connected') ? (
                      <span>
                        Pair the channel under{' '}
                        <Link to="/settings/whatsapp" className="text-emerald-700 underline">
                          Settings → WhatsApp Integration
                        </Link>
                        .
                      </span>
                    ) : null
                  }
                />
              )}
              <Form.Item
                name="whatsapp_group_id"
                rules={[{ required: true, message: 'Select a WhatsApp group' }]}
              >
                <Select
                  showSearch
                  placeholder="Select group from connected channel"
                  options={catalogOptions}
                  loading={catalogLoading}
                  notFoundContent={catalogLoading ? <Spin size="small" /> : 'No groups available'}
                  optionFilterProp="label"
                  onChange={onWhapiGroupSelect}
                />
              </Form.Item>
            </>
          )}

          <Form.Item
            name="group_name"
            label="Display name"
            rules={[{ required: true, message: 'Enter a group name' }]}
          >
            <Input maxLength={255} />
          </Form.Item>

          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>

          <Form.Item
            name="price_list_to_be_sent"
            label="Send price list deck"
            valuePropName="checked"
          >
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>

          <Form.Item
            name="bond_deck_to_be_sent"
            label="Send bond deck"
            valuePropName="checked"
          >
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>

          <Form.Item
            name="inventory_position_to_be_sent"
            label="Inventory position"
            valuePropName="checked"
            extra="Receive share purchase / shortfall summary images from Real Inventory."
          >
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>

          <Form.Item
            name="chatbot_enabled"
            label="Price chatbot enabled"
            valuePropName="checked"
            extra="When on, the bot replies to price and investor-note queries in this group."
          >
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>

          <Form.Item
            name="special_pricing_approval_enabled"
            label="Special pricing approval group"
            valuePropName="checked"
            extra="Only one group can receive Approve/Reject buttons for special price requests."
          >
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>

          <Form.Item
            name="rm_price_approval_enabled"
            label="RM price approval group"
            valuePropName="checked"
            extra="Only one group can receive RM price requests after payment is confirmed. Reply to the card with the price only."
          >
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>

          <Form.Item name="partner_id" label="Partner (optional)">
            <Select
              allowClear
              showSearch
              placeholder="Search partner by name, email, or mobile"
              filterOption={false}
              onSearch={searchUsers}
              loading={userSearchLoading}
              options={userOptions.map((u) => ({
                value: u.id,
                label: [u.name, u.email, u.mobile].filter(Boolean).join(' · '),
              }))}
            />
          </Form.Item>

          {hasLinkedPartner ? (
            <Form.Item label="Relationship Manager">
              {derivedRmLoading ? (
                <Spin size="small" />
              ) : derivedRmName ? (
                <Tag color="green" className="m-0 border-0 font-medium">
                  {derivedRmName}
                </Tag>
              ) : (
                <span className="text-gray-400 italic text-sm">Not assigned on partner</span>
              )}
            </Form.Item>
          ) : (
            <Form.Item
              name="rm_user_id"
              label="Relationship Manager"
              rules={[{ required: true, message: 'Select an RM' }]}
            >
              <Select
                showSearch
                placeholder={rmOptionsLoading ? 'Loading RMs…' : 'Select RM'}
                loading={rmOptionsLoading}
                disabled={rmOptionsLoading}
                optionFilterProp="label"
                notFoundContent={rmOptionsLoading ? <Spin size="small" /> : 'No RMs found'}
                options={rmOptions.map((r) => ({
                  label: r.entity_type ? `${r.name} (${r.entity_type})` : r.name,
                  value: r.uuid ?? r.id,
                }))}
              />
            </Form.Item>
          )}
        </Form>
      </Drawer>
    </DashboardPageWrapper>
  );
}
